import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { accommodations, poiCategories } from '../data/mockData';
import { calculateDistance } from '../utils/distanceCalculator';
import { ArrowLeftIcon, StarIcon, MapPinIcon, BedIcon, BathIcon, UsersIcon, WifiIcon, TvIcon, UtensilsIcon } from 'lucide-react';
// Fix for Leaflet marker icons
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Create a function to generate custom POI icons
const createPOIIcon = iconUrl => {
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};
import { useUserProfile, poiRelevanceByPartyType } from '../context/UserProfileContext';
export const AccommodationDetail = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [selectedPOIs, setSelectedPOIs] = useState<string[]>([]);
  const accommodation = accommodations.find(acc => acc.id === Number(id));
  const {
    travelParty
  } = useUserProfile();
  // Default to showing all POI categories on the detail page
  useEffect(() => {
    setSelectedPOIs(poiCategories.map(cat => cat.type));
  }, []);
  if (!accommodation) {
    return <div className="flex flex-col items-center justify-center h-screen" data-id="element-36">
        <h2 className="text-2xl font-bold mb-4" data-id="element-37">Accommodation not found</h2>
        <button onClick={() => navigate('/')} className="bg-rose-500 text-white px-4 py-2 rounded-lg hover:bg-rose-600 transition" data-id="element-38">
          Back to listings
        </button>
      </div>;
  }
  // Calculate nearby POIs with distances
  const nearbyPOIs = poiCategories.flatMap(category => {
    return category.items.map(poi => {
      const distance = calculateDistance(accommodation.lat, accommodation.lng, poi.lat, poi.lng);
      return {
        ...poi,
        distance,
        categoryType: category.type,
        categoryLabel: category.label,
        icon: category.icon
      };
    });
  }).sort((a, b) => a.distance - b.distance).slice(0, 15); // Show the 15 closest POIs
  // Group POIs by category for display, prioritizing relevant ones for the travel party
  const poiByCategory = nearbyPOIs.reduce((acc, poi) => {
    if (!acc[poi.categoryLabel]) {
      acc[poi.categoryLabel] = [];
    }
    acc[poi.categoryLabel].push(poi);
    return acc;
  }, {});
  // Get relevant POI types for current travel party
  const relevantPOITypes = poiRelevanceByPartyType[travelParty];
  // Sort categories by relevance to travel party
  const sortedCategories = Object.entries(poiByCategory).sort(([catA], [catB]) => {
    // Find if category matches a relevant POI type
    const poiA = nearbyPOIs.find(poi => poi.categoryLabel === catA);
    const poiB = nearbyPOIs.find(poi => poi.categoryLabel === catB);
    if (!poiA || !poiB) return 0;
    const aRelevanceIndex = relevantPOITypes.indexOf(poiA.categoryType);
    const bRelevanceIndex = relevantPOITypes.indexOf(poiB.categoryType);
    // If both are relevant, sort by relevance index
    if (aRelevanceIndex !== -1 && bRelevanceIndex !== -1) {
      return aRelevanceIndex - bRelevanceIndex;
    }
    // If only one is relevant, prioritize it
    if (aRelevanceIndex !== -1) return -1;
    if (bRelevanceIndex !== -1) return 1;
    // If neither is relevant, keep original order
    return 0;
  });
  // Get persona-specific label for heading
  const personaLabel = travelParty === 'couple' ? 'Couples' : travelParty === 'youngster' ? 'Young Travelers' : 'Families';
  return <div className="bg-white min-h-screen" data-id="element-39">
      {/* Back button */}
      <div className="bg-white p-4 border-b border-gray-200 sticky top-0 z-10" data-id="element-40">
        <Link to="/" className="flex items-center text-rose-500 hover:text-rose-600 transition" data-id="element-41">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-42" />
          <span className="font-medium" data-id="element-43">Back to listings</span>
        </Link>
      </div>
      <div className="max-w-6xl mx-auto p-4" data-id="element-44">
        {/* Header section */}
        <div className="mb-6" data-id="element-45">
          <h1 className="text-3xl font-bold mb-2" data-id="element-46">{accommodation.title}</h1>
          <div className="flex items-center justify-between" data-id="element-47">
            <div className="flex items-center gap-2" data-id="element-48">
              <div className="flex items-center" data-id="element-49">
                <StarIcon size={18} className="fill-current text-black" data-id="element-50" />
                <span className="ml-1 font-medium" data-id="element-51">{accommodation.rating}</span>
              </div>
              <span className="text-gray-500" data-id="element-52">•</span>
              <div className="text-gray-700" data-id="element-53">{accommodation.location}</div>
            </div>
            <div className="text-2xl font-bold text-rose-500" data-id="element-54">
              ${accommodation.price}
              <span className="text-sm font-normal text-gray-500" data-id="element-55">/night</span>
            </div>
          </div>
        </div>
        {/* Image and details section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-56">
          <div className="rounded-xl overflow-hidden h-80" data-id="element-57">
            <img src={accommodation.image} alt={accommodation.title} className="w-full h-full object-cover" data-id="element-58" />
          </div>
          <div className="space-y-6" data-id="element-59">
            <div data-id="element-60">
              <h2 className="text-xl font-bold mb-2" data-id="element-61">Description</h2>
              <p className="text-gray-700" data-id="element-62">{accommodation.description}</p>
            </div>
            <div data-id="element-63">
              <h2 className="text-xl font-bold mb-2" data-id="element-64">Amenities</h2>
              <div className="grid grid-cols-2 gap-4" data-id="element-65">
                <div className="flex items-center gap-2" data-id="element-66">
                  <BedIcon size={18} data-id="element-67" />
                  <span data-id="element-68">2 Bedrooms</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-69">
                  <BathIcon size={18} data-id="element-70" />
                  <span data-id="element-71">1 Bathroom</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-72">
                  <UsersIcon size={18} data-id="element-73" />
                  <span data-id="element-74">4 Guests</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-75">
                  <WifiIcon size={18} data-id="element-76" />
                  <span data-id="element-77">Free WiFi</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-78">
                  <TvIcon size={18} data-id="element-79" />
                  <span data-id="element-80">TV</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-81">
                  <UtensilsIcon size={18} data-id="element-82" />
                  <span data-id="element-83">Kitchen</span>
                </div>
              </div>
            </div>
            <div data-id="element-84">
              <h2 className="text-xl font-bold mb-2" data-id="element-85">Location</h2>
              <p className="text-gray-700" data-id="element-86">
                {accommodation.location}, Amsterdam
              </p>
              <p className="text-gray-500 text-sm mt-1" data-id="element-87">
                Coordinates: {accommodation.lat.toFixed(6)},{' '}
                {accommodation.lng.toFixed(6)}
              </p>
            </div>
            <button className="w-full bg-rose-500 text-white py-3 rounded-lg hover:bg-rose-600 transition font-medium" data-id="element-88">
              Book this place
            </button>
          </div>
        </div>
        {/* Map section */}
        <div className="mb-8" data-id="element-89">
          <h2 className="text-xl font-bold mb-4" data-id="element-90">Location & Nearby Places</h2>
          <div className="h-96 rounded-xl overflow-hidden" data-id="element-91">
            <MapContainer center={[accommodation.lat, accommodation.lng]} zoom={15} style={{
            height: '100%',
            width: '100%'
          }} data-id="element-92">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-93" />
              {/* Accommodation marker */}
              <Marker position={[accommodation.lat, accommodation.lng]} icon={defaultIcon} data-id="element-94">
                <Popup data-id="element-95">
                  <div className="font-semibold" data-id="element-96">{accommodation.title}</div>
                  <div className="text-sm text-gray-600" data-id="element-97">
                    ${accommodation.price} / night
                  </div>
                </Popup>
              </Marker>
              {/* POI markers */}
              {nearbyPOIs.filter(poi => selectedPOIs.includes(poi.categoryType)).map((poi, index) => <Marker key={`${poi.categoryType}-${index}`} position={[poi.lat, poi.lng]} icon={createPOIIcon(poi.icon)} data-id="element-98">
                    <Popup data-id="element-99">
                      <div className="font-semibold" data-id="element-100">{poi.name}</div>
                      <div className="text-sm text-gray-600" data-id="element-101">
                        {poi.categoryLabel}
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-102">
                        {poi.distance.toFixed(2)} km away
                      </div>
                    </Popup>
                  </Marker>)}
            </MapContainer>
          </div>
        </div>
        {/* Nearby POIs section */}
        <div className="mb-8" data-id="element-103">
          <h2 className="text-xl font-bold mb-4" data-id="element-104">
            Points of Interest for {personaLabel}
          </h2>
          <div className="space-y-6" data-id="element-105">
            {sortedCategories.map(([category, pois]) => {
            const poi = (pois as any[])[0];
            const isRelevant = relevantPOITypes.includes(poi.categoryType);
            return <div key={category} className={isRelevant ? 'bg-rose-50 p-4 rounded-lg' : ''} data-id="element-106">
                  <h3 className={`font-medium text-lg mb-2 ${isRelevant ? 'text-rose-600' : ''}`} data-id="element-107">
                    {category}
                    {isRelevant && ' ★'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-108">
                    {(pois as any[]).map((poi, index) => <div key={`${poi.categoryType}-${index}`} className={`${isRelevant ? 'bg-white' : 'bg-gray-50'} p-3 rounded-lg flex items-start gap-3`} data-id="element-109">
                        <img src={poi.icon} alt={poi.categoryLabel} className="w-6 h-6 mt-1" data-id="element-110" />
                        <div data-id="element-111">
                          <div className="font-medium" data-id="element-112">{poi.name}</div>
                          <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-113">
                            <MapPinIcon size={14} className="mr-1" data-id="element-114" />
                            <span data-id="element-115">{poi.distance.toFixed(2)} km away</span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
};